import pWaitFor from 'p-wait-for';
import { Linking } from 'react-native';
import i18next from 'i18next';

import { delay } from '@utils';
import AppStateService from './AppStateService';
import AlertService from './AlertService';

export const PermissionStatus = {
  GRANTED: 'granted',
  UNDETERMINED: 'undetermined',
  DENIED: 'denied',
} as const;

type PermissionStatusType =
  typeof PermissionStatus[keyof typeof PermissionStatus];

export interface IPermissionResponse {
  /**
   * Determines the status of the permission.
   */
  status: PermissionStatusType;
}

type DirectToSettingsDeniedType = {
  directToSettings: true;
  title: string;
  message: string;
};

type NotDirectToSettingsDeniedType = {
  directToSettings: false;
  title?: string;
  message?: string;
};

type DeniedType =
  | DirectToSettingsDeniedType
  | NotDirectToSettingsDeniedType;

export interface IPermissionRequestBehavior {
  waitForSet?: boolean;
  denied?: DeniedType;
}

type MethodsType<
  TResponse extends IPermissionResponse,
  TOptions extends object,
> = {
  getPermission: (options?: TOptions) => Promise<TResponse>;
  requestPermission: (options?: TOptions) => Promise<TResponse>;
};

type OptionsType<TOptions extends object> =
  IPermissionRequestBehavior & TOptions;

class PermissionService {
  request<
    TResponse extends IPermissionResponse,
    TOptions extends object,
  >(
    methods: MethodsType<TResponse, TOptions>,
    options?: OptionsType<TOptions>,
  ) {
    return this._askForPermission(
      methods,
      options ?? ({} as OptionsType<TOptions>),
    );
  }

  /**
   * Request permission natively or manually or skip the request if permission is granted
   */
  private async _askForPermission<
    TResponse extends IPermissionResponse,
    TOptions extends object,
  >(
    methods: MethodsType<TResponse, TOptions>,
    options: OptionsType<TOptions>,
  ) {
    const {
      waitForSet = true,
      denied,
      ...permissionOptions
    } = options;

    // check if we have permission options for passing into permission methods
    const permissionOptionsToPass = Object.keys(permissionOptions)
      .length
      ? (permissionOptions as TOptions)
      : undefined;

    // trying to get exist permission
    const res: TResponse = await methods.getPermission(
      permissionOptionsToPass,
    );

    // if permission was granted, then we can just return
    if (res.status === PermissionStatus.GRANTED) {
      return res;
    }

    // check if permission can be asked again before
    // if can - request permission natively
    if (res.status === PermissionStatus.UNDETERMINED) {
      return this._askPermissionNatively(() =>
        methods.requestPermission(permissionOptionsToPass),
      );
    } else if (denied?.directToSettings) {
      // else if permission can't be asked again
      // we need to request permission manually
      return this._askPermissionManually(
        () => methods.getPermission(permissionOptionsToPass),
        {
          denied,
          waitForSet,
        },
      );
    }

    return res;
  }

  /**
   * Request permission natively - show default request permission prompt
   */
  private async _askPermissionNatively<
    TResponse extends IPermissionResponse,
  >(requestPermission: () => Promise<TResponse>) {
    const res = await requestPermission();
    // Do extra processing here
    return res;
  }

  /**
   * Request permission manually - show redirect to settings prompt
   */
  private async _askPermissionManually<
    TResponse extends IPermissionResponse,
  >(
    getPermission: () => Promise<TResponse>,
    behavior: Omit<IPermissionRequestBehavior, 'denied'> & {
      denied: DirectToSettingsDeniedType;
    },
  ) {
    // show alert with ability to redirect to settings
    const isWait = await this._showDeniedAlert(
      behavior.denied.title,
      behavior.denied.message,
    );

    // awaiting while the user goes back to the app from the settings
    if (behavior.waitForSet && isWait) {
      await this._waitForPermission();
    }

    // checking permission again
    return getPermission();
  }

  private _showDeniedAlert(title: string, message: string) {
    return new Promise<boolean>((res) => {
      const onPress = () => {
        // navigate to apps settings
        Linking.openSettings();

        // and resolve the promise
        res(true);
      };

      const showAlert = () =>
        AlertService.showAlert(
          title,
          message,
          [
            {
              text: i18next.t('services.permission.cancel'),
              style: 'cancel',
              onPress: () => res(false),
            },
            {
              text: i18next.t('services.permission.settings'),
              onPress,
            },
          ],
          { cancelable: false },
        );

      setTimeout(showAlert, 100);
    });
  }

  /**
   * Waiting for user to set permission in the settings
   */
  private async _waitForPermission(): Promise<void> {
    // waiting for the app to go background
    await delay(1000);
    // waiting for the app to go foreground (active)
    await pWaitFor(() => AppStateService.isActive);
    // waiting a little bit for animation
    await delay(1000);
  }
}

export default new PermissionService();
